import { T } from '../../styles/theme'
import XAxisLabels from './XAxisLabels'

export default function BalanceChart({ data, minTarget, maxTarget, onBarClick, theme = T, width = 320, height = 140 }) {
  const W = width, H = height, PL = 44, PB = 28, PT = 12, PR = 28
  const cW = W - PL - PR, cH = H - PB - PT

  if (!data || data.length < 2) return (
    <div style={{ textAlign: 'center', color: theme.muted, padding: 20, fontSize: 13 }}>
      Necesitas al menos 2 días de registro para ver la gráfica
    </div>
  )

  // Los días sin datos no cuentan para la escala: de lo contrario un día en
  // blanco (balance = -TDEE) estiraría el eje y aplastaría al resto.
  const vals = data.filter(d => !d.sinDatos).map(d => d.v)
  // El 0 siempre debe verse con margen a ambos lados, aunque todos los
  // valores (datos y franja objetivo) estén del mismo lado del cero.
  const allVals = [...vals, minTarget - 100, maxTarget + 100, 150, -150]
  const minV = Math.min(...allVals)
  const maxV = Math.max(...allVals)
  const n = data.length
  const xScale = i => PL + (i / (n - 1)) * cW
  const yScale = v => PT + (1 - (v - minV) / (maxV - minV)) * cH
  const zeroY = yScale(0)
  const minTY = yScale(minTarget)
  const maxTY = yScale(maxTarget)
  const clipId = `balance-clip-${width}-${height}`

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
      <defs><clipPath id={clipId}><rect x={PL} y={PT} width={cW} height={cH} /></clipPath></defs>
      <rect x={PL} y={maxTY} width={cW} height={minTY - maxTY} fill={theme.accentSoft ?? 'rgba(74,222,128,0.08)'} clipPath={`url(#${clipId})`} />
      <line x1={PL} x2={W - PR} y1={minTY} y2={minTY} stroke={theme.accent} strokeWidth="1" strokeDasharray="4,3" opacity=".4" />
      <line x1={PL} x2={W - PR} y1={maxTY} y2={maxTY} stroke={theme.accent} strokeWidth="1" strokeDasharray="4,3" opacity=".4" />
      <line x1={PL} x2={W - PR} y1={zeroY} y2={zeroY} stroke={theme.muted} strokeWidth="1" opacity=".3" />
      <text x={PL - 4} y={zeroY + 4} textAnchor="end" fill={theme.muted} fontSize="9" fontFamily="DM Mono, monospace">0</text>
      <text x={PL - 4} y={PT + 8} textAnchor="end" fill={theme.muted} fontSize="9" fontFamily="DM Mono, monospace">{Math.round(maxV)}</text>
      <text x={PL - 4} y={H - PB - 2} textAnchor="end" fill={theme.muted} fontSize="9" fontFamily="DM Mono, monospace">{Math.round(minV)}</text>
      {data.map((d, i) => {
        const x = xScale(i)
        if (d.sinDatos) {
          return (
            <g key={i} style={onBarClick ? { cursor: 'pointer' } : undefined} onClick={onBarClick ? () => onBarClick(d, i) : undefined}>
              <rect x={x - 8} y={PT} width={16} height={cH} fill={theme.muted} opacity=".18" rx="3" clipPath={`url(#${clipId})`} />
            </g>
          )
        }
        const isIn = d.v >= minTarget && d.v <= maxTarget
        const barY = d.v >= 0 ? yScale(d.v) : zeroY
        const barH = Math.abs(yScale(d.v) - zeroY)
        // Verde = dentro del objetivo. Rojo = alarma (superávit, o
        // déficit muy por encima de lo previsto). Azul = desviación
        // leve, un déficit algo mayor del objetivo pero cerca del borde.
        const col = isIn
          ? theme.accent
          : d.v > maxTarget ? theme.danger
          : d.v < minTarget - 50 ? theme.danger
          : theme.blue
        return (
          <g key={i} style={onBarClick ? { cursor: 'pointer' } : undefined} onClick={onBarClick ? () => onBarClick(d, i) : undefined}>
            <rect x={x - 7} y={barY} width={14} height={barH || 2} fill={col} rx="3" opacity=".85" clipPath={`url(#${clipId})`} />
          </g>
        )
      })}
      <XAxisLabels data={data} usableWidth={cW} xScale={xScale} left={PL} right={W - PR} y={H - 4} theme={theme} />
      <line x1={PL} x2={PL} y1={PT} y2={H - PB} stroke={theme.cardB} strokeWidth="1" />
      <line x1={PL} x2={W - PR} y1={H - PB} y2={H - PB} stroke={theme.cardB} strokeWidth="1" />
    </svg>
  )
}
